import React, { useMemo, useState } from "react";
import { TEMPLATE_COLUMNS } from "./reportColumnCatalog";

export default function ReportTemplateColumnsEditor({
  columnOrder = [],
  visibleColumns = {},
  columnLabels = {},
  onChange,
}) {
  const fullOrder = useMemo(() => {
    const have = new Set(columnOrder);
    const missing = TEMPLATE_COLUMNS.map((c) => c.viewKey).filter(
      (k) => !have.has(k),
    );
    return [...columnOrder, ...missing];
  }, [columnOrder]);

  const [dragOverKey, setDragOverKey] = useState(null);

  const reorder = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;

    const order = [...fullOrder];
    const sourceIndex = order.indexOf(sourceKey);
    const targetIndex = order.indexOf(targetKey);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const [movedKey] = order.splice(sourceIndex, 1);
    order.splice(targetIndex, 0, movedKey);

    emit({ columnOrder: order });
  };

  const byKey = useMemo(() => {
    const m = {};
    for (const c of TEMPLATE_COLUMNS) m[c.viewKey] = c;
    return m;
  }, []);

  const emit = (next) => {
    onChange?.({
      columnOrder: next.columnOrder ?? fullOrder,
      visibleColumns: next.visibleColumns ?? visibleColumns,
      columnLabels: next.columnLabels ?? columnLabels,
    });
  };

  const toggle = (viewKey) => {
    emit({
      visibleColumns: {
        ...visibleColumns,
        [viewKey]: !visibleColumns[viewKey],
      },
    });
  };

  const setLabel = (viewKey, val) => {
    emit({ columnLabels: { ...columnLabels, [viewKey]: val } });
  };

  const move = (viewKey, dir) => {
    const order = [...fullOrder];
    const idx = order.indexOf(viewKey);
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= order.length) return;
    const [item] = order.splice(idx, 1);
    order.splice(target, 0, item);
    emit({ columnOrder: order });
  };

  const visibleCount = fullOrder.filter(
    (k) => visibleColumns[k] !== false,
  ).length;

  return (
    <div
      className="r6-tpl-cols"
      onDragOver={(e) => {
        const edgeSize = 90;
        const scrollSpeed = 18;

        if (e.clientY < edgeSize) {
          window.scrollBy({
            top: -scrollSpeed,
            behavior: "auto",
          });
        } else if (window.innerHeight - e.clientY < edgeSize) {
          window.scrollBy({
            top: scrollSpeed,
            behavior: "auto",
          });
        }
      }}
    >
      <div className="r6-tpl-cols-head">
        <div className="r6-tpl-cols-title">
          Columns
          <span className="r6-tpl-cols-count">
            {visibleCount} of {TEMPLATE_COLUMNS.length}
          </span>
        </div>
        <button
          type="button"
          className="r6-link"
          onClick={() => {
            const reset = {};
            for (const c of TEMPLATE_COLUMNS) reset[c.viewKey] = true;
            emit({
              columnOrder: TEMPLATE_COLUMNS.map((c) => c.viewKey),
              visibleColumns: reset,
              columnLabels: {},
            });
          }}
        >
          Reset
        </button>
      </div>

      <ul className="r6-tpl-cols-list">
        {fullOrder.map((viewKey, i) => {
          const def = byKey[viewKey];
          if (!def) return null;
          const enabled = visibleColumns[viewKey] !== false;
          const label = columnLabels[viewKey] || def.label;
          return (
            <li
              key={viewKey}
              className={`r6-tpl-col-row ${enabled ? "" : "is-off"} ${
                dragOverKey === viewKey ? "is-drag-over" : ""
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDragOverKey(viewKey);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const sourceKey = e.dataTransfer.getData("text/plain");
                reorder(sourceKey, viewKey);
                setDragOverKey(null);
              }}
            >
              <button
                type="button"
                className="r6-tpl-drag-handle"
                draggable
                title="Drag to reorder column"
                aria-label={`Drag ${label} to reorder`}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", viewKey);
                  setDragOverKey(null);
                }}
                onDragEnd={() => setDragOverKey(null)}
              >
                <i className="fas fa-grip-vertical" />
              </button>

              <label className="r6-tpl-col-check">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={() => toggle(viewKey)}
                />
              </label>
              <span className="r6-tpl-col-group">{def.group}</span>
              <input
                className="form-control form-control-sm r6-tpl-col-label"
                value={label}
                placeholder={def.label}
                onChange={(e) => setLabel(viewKey, e.target.value)}
                disabled={!enabled}
              />
              <div className="r6-tpl-col-moves">
                <button
                  type="button"
                  className="r6-tpl-move"
                  onClick={() => move(viewKey, "up")}
                  disabled={i === 0}
                  title="Move up"
                >
                  <i className="fas fa-chevron-up" />
                </button>
                <button
                  type="button"
                  className="r6-tpl-move"
                  onClick={() => move(viewKey, "down")}
                  disabled={i === fullOrder.length - 1}
                  title="Move down"
                >
                  <i className="fas fa-chevron-down" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
